import React from 'react';

interface InventorySlotButtonProps {
  className?: string;
  indexLabel?: React.ReactNode;
  icon?: React.ReactNode;
  name?: React.ReactNode;
  countLabel?: React.ReactNode;
  capacityLabel?: React.ReactNode;
  typeLabel?: React.ReactNode;
  progress?: number;
  title?: string;
  ariaLabel?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onContextMenu?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  stackSquares?: number;
}

const clampProgress = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0;
  }

  return Math.max(0, Math.min(value, 1));
};

export const InventorySlotButton: React.FC<InventorySlotButtonProps> = ({
  className = '',
  indexLabel,
  icon = '·',
  name = 'Empty',
  countLabel = '',
  capacityLabel = '',
  typeLabel = '',
  progress = 0,
  title,
  ariaLabel,
  onClick,
  onContextMenu,
  disabled = false,
  stackSquares = 0,
}) => {
  const combinedClassName = ['inventory-slot', className]
    .filter(Boolean)
    .join(' ');
  const normalizedProgress = clampProgress(progress);
  const normalizedStackSquares = Number.isFinite(stackSquares)
    ? Math.max(0, Math.floor(stackSquares))
    : 0;

  return (
    <button
      type='button'
      className={combinedClassName}
      onClick={onClick}
      onContextMenu={onContextMenu}
      title={title}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      <span className='inventory-slot-index'>{indexLabel}</span>
      <span className='inventory-slot-icon'>{icon}</span>
      {normalizedStackSquares > 0 ? (
        <div className='inventory-slot-stack-indicator' aria-hidden='true'>
          {Array.from({ length: normalizedStackSquares }).map((_, index) => (
            <span key={index} className='inventory-slot-stack-square' />
          ))}
        </div>
      ) : null}
      <span className='inventory-slot-name'>{name}</span>
      <span className='inventory-slot-count'>{countLabel}</span>
      <span className='inventory-slot-capacity'>{capacityLabel}</span>
      <span className='inventory-slot-type'>{typeLabel}</span>
      <div className='inventory-slot-progress-track' aria-hidden='true'>
        <div
          className='inventory-slot-progress-fill'
          style={{ width: `${normalizedProgress * 100}%` }}
        />
      </div>
    </button>
  );
};

export default InventorySlotButton;
